/**
 * <sidebar-item> Web Component
 *
 * A sidebar menu item with expandable submenu, flyout support, and pin functionality.
 * Used in the workspace sidebar for navigation.
 *
 * Usage:
 *   <sidebar-item icon="luboo:building" label="Units" data-menu="units">
 *     <button data-action="units:add" data-pinnable>Add Unit</button>
 *     <button data-action="units:view-all" data-pinnable>View All Units</button>
 *   </sidebar-item>
 *
 * Attributes:
 *   - icon: Icon name for svg-icon component (e.g., "luboo:building")
 *   - label: Menu item text displayed next to icon
 *   - data-menu: Menu identifier
 *   - expanded: Whether submenu is open (reflected attribute)
 *   - no-submenu: For items without children (e.g., Help - just a trigger)
 *
 * Events:
 *   - sidebar-item-action: Fired when a submenu action is clicked
 *     detail: { action, pinnable }
 *   - sidebar-item-toggle: Fired when submenu is expanded/collapsed
 *     detail: { expanded }
 *   - sidebar-item-pin: Fired when pin icon is clicked
 *     detail: { action, pinned }
 */
class SidebarItemElement extends HTMLElement {
  static observedAttributes = ['icon', 'label', 'expanded', 'no-submenu'];

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // State
    this._expanded = false;
    this._flyoutVisible = false;
    this._flyoutElement = null;
    this._hoverTimeout = null;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
  }

  disconnectedCallback() {
    this.closeFlyout();
    if (this._hoverTimeout) {
      clearTimeout(this._hoverTimeout);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    switch (name) {
      case 'icon':
        this.updateIcon();
        break;
      case 'label':
        this.updateLabel();
        break;
      case 'expanded':
        this._expanded = this.hasAttribute('expanded');
        this.updateExpandedState();
        break;
    }
  }

  // Public API
  get expanded() {
    return this._expanded;
  }

  set expanded(value) {
    this._expanded = Boolean(value);
    if (this._expanded) {
      this.setAttribute('expanded', '');
    } else {
      this.removeAttribute('expanded');
    }
  }

  get icon() {
    return this.getAttribute('icon') || '';
  }

  set icon(value) {
    this.setAttribute('icon', value);
  }

  get label() {
    return this.getAttribute('label') || '';
  }

  set label(value) {
    this.setAttribute('label', value);
  }

  get noSubmenu() {
    return this.hasAttribute('no-submenu');
  }

  /**
   * Check if the parent sidebar is in collapsed mode
   */
  get isCollapsedMode() {
    const sidebar = this.closest('.workspace-sidebar');
    if (!sidebar) return false;
    return sidebar.dataset.collapsed === 'true' || sidebar.hasAttribute('data-collapsed');
  }

  render() {
    const icon = this.getAttribute('icon') || '';
    const label = this.getAttribute('label') || '';
    const hasSubmenu = !this.hasAttribute('no-submenu');

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: relative;
        }

        .trigger {
          display: flex;
          align-items: center;
          gap: calc(8px * var(--density-multiplier, 1));
          padding: calc(6px * var(--density-multiplier, 1)) calc(12px * var(--density-multiplier, 1));
          cursor: pointer;
          color: var(--text-color, #ddd);
          background: transparent;
          border: none;
          /* Inset from sidebar edges so the rounded hover bg has air around it.
           * Width compensates so the trigger doesn't overflow its host. */
          margin: 0 calc(4px * var(--density-multiplier, 1));
          width: calc(100% - 8px * var(--density-multiplier, 1));
          border-radius: var(--radius-md, 8px);
          box-sizing: border-box;
          text-align: left;
          font: inherit;
          font-size: var(--sidebar-font-size, 0.8125rem);
          transition: background-color 0.15s;
          white-space: nowrap;
          overflow: hidden;
        }

        .trigger:hover {
          background-color: var(--highlight-color, #5680c2);
        }

        .trigger:focus-visible {
          outline: 2px solid var(--highlight-color, #5680c2);
          outline-offset: -2px;
        }

        .icon-wrapper {
          flex-shrink: 0;
          width: var(--sidebar-icon-size, 16px);
          height: var(--sidebar-icon-size, 16px);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .icon-wrapper ::slotted(svg-icon),
        .icon-wrapper svg-icon {
          width: var(--sidebar-icon-size, 16px);
          height: var(--sidebar-icon-size, 16px);
        }

        .icon-wrapper ::slotted(topographic-identicon-or-image) {
          width: var(--sidebar-icon-size, 16px);
          height: var(--sidebar-icon-size, 16px);
          border-radius: 50%;
          overflow: hidden;
        }

        .label {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: opacity 0.2s, width 0.2s;
        }

        :host-context(.workspace-sidebar[data-collapsed="true"]) .label,
        :host-context(.workspace-sidebar[data-collapsed]) .label {
          opacity: 0;
          width: 0;
          overflow: hidden;
        }

        .chevron {
          flex-shrink: 0;
          width: 12px;
          height: 12px;
          transition: transform 0.2s;
          color: var(--text-color, #ddd);
          opacity: 0.7;
        }

        :host([expanded]) .chevron {
          transform: rotate(90deg);
        }

        :host-context(.workspace-sidebar[data-collapsed="true"]) .chevron,
        :host-context(.workspace-sidebar[data-collapsed]) .chevron {
          display: none;
        }

        :host([no-submenu]) .chevron {
          display: none;
        }

        .submenu {
          display: none;
          background-color: color-mix(in srgb, var(--bg-color, #2a2a2a) 50%, var(--header-color, #383838));
          overflow: hidden;
        }

        :host([expanded]) .submenu {
          display: block;
        }

        :host-context(.workspace-sidebar[data-collapsed="true"]) .submenu,
        :host-context(.workspace-sidebar[data-collapsed]) .submenu {
          display: none !important;
        }

        .submenu-content {
          padding: calc(2px * var(--density-multiplier, 1)) 0;
        }

        /* Slotted button styling — rounded bg inset from edges, mirroring
         * the .trigger pattern but with a tighter radius for sub-items. */
        ::slotted(button) {
          display: flex;
          align-items: center;
          gap: calc(6px * var(--density-multiplier, 1));
          margin: 0 calc(4px * var(--density-multiplier, 1));
          width: calc(100% - 8px * var(--density-multiplier, 1));
          border-radius: var(--radius-sm, 4px);
          box-sizing: border-box;
          padding: calc(5px * var(--density-multiplier, 1)) calc(12px * var(--density-multiplier, 1)) calc(5px * var(--density-multiplier, 1)) calc(36px * var(--density-multiplier, 1));
          background: none;
          border: none;
          color: var(--text-color, #ddd);
          cursor: pointer;
          text-align: left;
          font: inherit;
          font-size: var(--sidebar-font-size, 0.8125rem);
          position: relative;
        }

        /* Note: ::slotted(button:hover) doesn't work, use light DOM styles in sidebar.css */

        /* Pin icon styling - handled by wrapper in submenu */
        .submenu-item-wrapper {
          display: flex;
          align-items: center;
          position: relative;
        }

        .submenu-item-wrapper ::slotted(button) {
          flex: 1;
        }

        .pin-btn {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          padding: calc(2px * var(--density-multiplier, 1));
          background: none;
          border: none;
          color: var(--text-color, #ddd);
          opacity: 0;
          cursor: pointer;
          transition: opacity 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
        }

        .submenu-item-wrapper:hover .pin-btn {
          opacity: 0.6;
        }

        .pin-btn:hover {
          opacity: 1 !important;
        }

        .pin-btn.pinned {
          opacity: 1;
          color: var(--highlight-color, #5680c2);
        }

        .pin-btn svg-icon {
          width: 14px;
          height: 14px;
        }

        /* Mobile: show pin buttons always (no hover on touch) */
        @media (max-width: 768px) {
          .pin-btn {
            opacity: 0.15 !important;
          }
          .pin-btn.pinned {
            opacity: 1 !important;
          }
        }
      </style>

      <button class="trigger" part="trigger">
        <span class="icon-wrapper">
          ${icon ? `<svg-icon icon="${icon}"></svg-icon>` : '<slot name="icon"></slot>'}
        </span>
        <span class="label">${label}</span>
        ${hasSubmenu ? `
          <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        ` : ''}
      </button>

      <div class="submenu" part="submenu">
        <div class="submenu-content">
          <slot></slot>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    const trigger = this.shadowRoot.querySelector('.trigger');

    // Click to toggle submenu (or show flyout if collapsed)
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();

      if (this.noSubmenu) {
        // No submenu - dispatch action for the item itself
        const action = this.dataset.action;
        if (action) {
          this.dispatchEvent(new CustomEvent('sidebar-item-action', {
            bubbles: true,
            composed: true,
            detail: { action }
          }));
        }
        return;
      }

      // In collapsed mode, show flyout instead of expanding inline
      if (this.isCollapsedMode) {
        // Toggle flyout on click (instead of just hover)
        if (this._flyoutVisible) {
          this.closeFlyout();
        } else {
          this.showFlyout();
        }
        return;
      }

      this.toggle();
    });

    // Hover for flyout in collapsed mode
    trigger.addEventListener('mouseenter', () => {
      if (this.isCollapsedMode && !this.noSubmenu) {
        this._hoverTimeout = setTimeout(() => {
          this.showFlyout();
        }, 150);
      }
    });

    trigger.addEventListener('mouseleave', (e) => {
      if (this._hoverTimeout) {
        clearTimeout(this._hoverTimeout);
        this._hoverTimeout = null;
      }

      // Don't close if moving to flyout
      const relatedTarget = e.relatedTarget;
      if (relatedTarget && this._flyoutElement && this._flyoutElement.contains(relatedTarget)) {
        return;
      }

      // Delay closing flyout to allow moving to it
      setTimeout(() => {
        if (!this._flyoutElement?.matches(':hover') && !trigger.matches(':hover')) {
          this.closeFlyout();
        }
      }, 100);
    });

    // Handle slot changes to wrap pinnable items
    const slot = this.shadowRoot.querySelector('slot:not([name])');
    slot.addEventListener('slotchange', () => {
      this.setupPinnableItems();
    });

    // Delegate click events from slotted buttons
    this.addEventListener('click', (e) => {
      const button = e.target.closest('button[data-action]');
      if (button && this.contains(button)) {
        const action = button.dataset.action;
        const pinnable = button.hasAttribute('data-pinnable');

        this.dispatchEvent(new CustomEvent('sidebar-item-action', {
          bubbles: true,
          composed: true,
          detail: { action, pinnable }
        }));

        // Close flyout after action
        this.closeFlyout();
      }
    });
  }

  /**
   * Toggle submenu expanded state
   */
  toggle() {
    this.expanded = !this.expanded;

    this.dispatchEvent(new CustomEvent('sidebar-item-toggle', {
      bubbles: true,
      composed: true,
      detail: { expanded: this.expanded }
    }));
  }

  /**
   * Update icon element
   */
  updateIcon() {
    const iconWrapper = this.shadowRoot.querySelector('.icon-wrapper');
    if (!iconWrapper) return;

    const icon = this.getAttribute('icon');
    if (icon) {
      iconWrapper.innerHTML = `<svg-icon icon="${icon}"></svg-icon>`;
    }
  }

  /**
   * Update label text
   */
  updateLabel() {
    const labelEl = this.shadowRoot.querySelector('.label');
    if (labelEl) {
      labelEl.textContent = this.getAttribute('label') || '';
    }
  }

  /**
   * Update visual expanded state
   */
  updateExpandedState() {
    const submenu = this.shadowRoot.querySelector('.submenu');
    if (!submenu) return;

    if (this._expanded) {
      submenu.style.display = 'block';
    } else {
      submenu.style.display = '';
    }
  }

  /**
   * Setup pinnable items with pin buttons
   */
  setupPinnableItems() {
    // Get pinned items from localStorage
    const pinnedItems = this.getPinnedItems();

    // Find all pinnable buttons in light DOM
    const buttons = this.querySelectorAll('button[data-pinnable]');

    buttons.forEach(button => {
      // Check if already wrapped
      if (button.parentElement?.classList.contains('submenu-item-wrapper')) return;

      const action = button.dataset.action;
      const isPinned = pinnedItems.includes(action);

      // Create wrapper with pin button
      const wrapper = document.createElement('div');
      wrapper.className = 'submenu-item-wrapper';

      const pinBtn = document.createElement('button');
      pinBtn.className = `pin-btn ${isPinned ? 'pinned' : ''}`;
      pinBtn.setAttribute('title', isPinned ? 'Unpin' : 'Pin to sidebar');
      pinBtn.innerHTML = `<svg-icon icon="luboo:${isPinned ? 'pin' : 'pin-off'}"></svg-icon>`;

      pinBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.togglePin(action, pinBtn, button);
      });

      // Wrap button
      button.parentNode.insertBefore(wrapper, button);
      wrapper.appendChild(button);
      wrapper.appendChild(pinBtn);
    });
  }

  /**
   * Gets the scoped localStorage key for pinned items based on data-pinned-scope attribute
   * @returns {string} localStorage key for pinned items
   */
  _getPinnedStorageKey() {
    const container = document.querySelector('.workspace-container');
    const scope = container?.dataset.pinnedScope;
    if (scope) {
      return `luboo-sidebar-pinned-${scope}`;
    }
    // Fallback to global key if no scope defined
    return 'luboo-sidebar-pinned';
  }

  /**
   * Get pinned action names from localStorage (scoped by page type)
   * @returns {string[]} Array of pinned action names
   */
  getPinnedItems() {
    try {
      const key = this._getPinnedStorageKey();
      const stored = localStorage.getItem(key);
      if (!stored) return [];
      const items = JSON.parse(stored);
      // Extract action names from objects
      return items.map(item => item.action);
    } catch {
      return [];
    }
  }

  /**
   * Toggle pin state for an action
   * @param {string} action - The action name
   * @param {HTMLElement} pinBtn - The pin button element
   * @param {HTMLElement} button - The button being pinned (to get label)
   */
  togglePin(action, pinBtn, button) {
    const pinnedItems = this.getPinnedItems();
    const isPinned = pinnedItems.includes(action);

    // Get label from button text content
    const label = button?.textContent?.trim() || action.split(':').pop();
    // Get icon from parent sidebar-item
    const icon = this.icon;

    if (isPinned) {
      pinBtn.classList.remove('pinned');
      pinBtn.title = 'Pin to sidebar';
      pinBtn.querySelector('svg-icon').setAttribute('icon', 'luboo:pin-off');
    } else {
      pinBtn.classList.add('pinned');
      pinBtn.title = 'Unpin';
      pinBtn.querySelector('svg-icon').setAttribute('icon', 'luboo:pin');
    }

    // Let workspace-manager handle localStorage - just dispatch the event
    this.dispatchEvent(new CustomEvent('sidebar-item-pin', {
      bubbles: true,
      composed: true,
      detail: { action, label, icon, pinned: !isPinned }
    }));
  }

  /**
   * Show flyout menu (for collapsed sidebar mode)
   */
  showFlyout() {
    if (this._flyoutVisible || this.noSubmenu) return;

    // Create flyout element
    const flyout = document.createElement('div');
    flyout.className = 'sidebar-flyout';
    flyout.innerHTML = `
      <div class="sidebar-flyout-header">${this.label}</div>
      <div class="sidebar-flyout-content"></div>
    `;

    // Clone submenu content into flyout
    const content = flyout.querySelector('.sidebar-flyout-content');
    const buttons = this.querySelectorAll('button[data-action]');
    buttons.forEach(btn => {
      const clone = btn.cloneNode(true);
      clone.style.paddingLeft = '12px'; // Reset indent for flyout
      content.appendChild(clone);
    });

    // Style the flyout
    Object.assign(flyout.style, {
      position: 'fixed',
      backgroundColor: 'var(--panel-header, #333)',
      border: '1px solid var(--border-color, #1a1a1a)',
      borderRadius: '0 4px 4px 0',
      boxShadow: '2px 2px 8px var(--shadow-color, rgba(0,0,0,0.3))',
      minWidth: '150px',
      zIndex: '1000',
      overflow: 'hidden'
    });

    // Style flyout header
    const header = flyout.querySelector('.sidebar-flyout-header');
    Object.assign(header.style, {
      padding: '6px 12px',
      fontWeight: '600',
      fontSize: 'var(--sidebar-font-size, 0.8125rem)',
      borderBottom: '1px solid var(--separator-color, #444)',
      color: 'var(--text-color, #ddd)'
    });

    // Style flyout content buttons
    const flyoutButtons = flyout.querySelectorAll('button');
    flyoutButtons.forEach(btn => {
      Object.assign(btn.style, {
        display: 'block',
        width: '100%',
        padding: '5px 12px',
        background: 'none',
        border: 'none',
        color: 'var(--text-color, #ddd)',
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
        fontSize: 'var(--sidebar-font-size, 0.8125rem)'
      });

      btn.addEventListener('mouseenter', () => {
        btn.style.backgroundColor = 'var(--highlight-color, #5680c2)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.backgroundColor = 'transparent';
      });

      btn.addEventListener('click', (e) => {
        const action = btn.dataset.action;
        if (action) {
          this.dispatchEvent(new CustomEvent('sidebar-item-action', {
            bubbles: true,
            composed: true,
            detail: { action }
          }));
          this.closeFlyout();
        }
      });
    });

    // Position flyout
    document.body.appendChild(flyout);
    const rect = this.getBoundingClientRect();
    const sidebar = this.closest('.workspace-sidebar');
    const sidebarRect = sidebar?.getBoundingClientRect() || { right: 64 };

    flyout.style.left = `${sidebarRect.right}px`;
    flyout.style.top = `${rect.top}px`;

    // Ensure flyout doesn't go off screen
    const flyoutRect = flyout.getBoundingClientRect();
    if (flyoutRect.bottom > window.innerHeight) {
      flyout.style.top = `${window.innerHeight - flyoutRect.height - 8}px`;
    }

    // Close flyout when mouse leaves
    flyout.addEventListener('mouseleave', (e) => {
      const trigger = this.shadowRoot.querySelector('.trigger');
      if (!trigger.matches(':hover')) {
        this.closeFlyout();
      }
    });

    this._flyoutElement = flyout;
    this._flyoutVisible = true;
  }

  /**
   * Close flyout menu
   */
  closeFlyout() {
    if (this._flyoutElement) {
      this._flyoutElement.remove();
      this._flyoutElement = null;
    }
    this._flyoutVisible = false;
  }
}

export { SidebarItemElement };
